import SparkMD5 from 'spark-md5';

export const CHUNK_SIZE = 4 * 1024 * 1024; // 4MB

export const STATUS_CHECK_INTERVAL = 2000; // 2 seconds
export const STATUS_RETRY_INTERVAL = 5000; // 5 seconds

export const MAX_RETRIES = 2;

export const isAbortError = (error: unknown): error is DOMException =>
  error instanceof DOMException && error.name === 'AbortError';

export const getFileNameWithoutExtension = (filename: string) =>
  filename.split('.').slice(0, -1).join('.') || filename;

export const getFileExtension = (filename: string) =>
  filename.split('.').pop()?.toLowerCase() || '';

export const isPdfFile = (filename: string) =>
  getFileExtension(filename) === 'pdf';

export const isOfficeModernFile = (filename: string) => {
  const ext = getFileExtension(filename);

  return [
    'xlsx',
    'xlsm',
    'xlsb',
    'docx',
    'docm',
    'pptx',
    'ppsx',
    'odp',
  ].includes(ext);
};

export const isOfficeLegacyFile = (filename: string) => {
  const ext = getFileExtension(filename);

  return ['doc', 'ppt', 'pps', 'pot'].includes(ext);
};

export const isImageFile = (filename: string) => {
  const extension = getFileExtension(filename);

  return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(
    extension
  );
};

export const isUnsupportedFormat = (filename?: string) => {
  if (!filename) return false;

  const extension = getFileExtension(filename);

  return ['xls', 'mov'].includes(extension);
};

export const isFilePreviewSupported = (filename: string) =>
  isPdfFile(filename) ||
  isImageFile(filename) ||
  isOfficeModernFile(filename) ||
  isOfficeLegacyFile(filename);

export const downloadFileFromUrl = (url: string, fileName?: string) => {
  const link = document.createElement('a');
  link.href = url;
  if (fileName) {
    link.download = fileName;
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const computeFileMd5 = async (
  file: File | Blob,
  chunkSize: number = CHUNK_SIZE
): Promise<Uint8Array> => {
  const spark = new SparkMD5.ArrayBuffer();
  let offset = 0;

  while (offset < file.size) {
    const slice = file.slice(offset, offset + chunkSize);
    const buffer = await slice.arrayBuffer();
    spark.append(buffer);
    offset += chunkSize;
  }

  const rawHash = spark.end(true);
  const bytes = new Uint8Array(rawHash.length);
  for (let i = 0; i < rawHash.length; i++) {
    bytes[i] = rawHash.charCodeAt(i);
  }

  return bytes;
};
